import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add token to requests
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('access_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle 401 errors
api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      localStorage.removeItem('access_token');
      localStorage.removeItem('user');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

export default api;

// Auth API
export const authAPI = {
  login: (email, password) => api.post('/auth/login', { email, password }),
  register: (data) => api.post('/auth/register', data),
  me: () => api.get('/auth/me'),
};

// Companies API
export const companiesAPI = {
  getAll: () => api.get('/companies'),
  create: (data) => api.post('/companies', data),
};

// Subsidiaries API (formerly Departments)
export const subsidiariesAPI = {
  getAll: (companyId) => api.get(`/subsidiaries?company_id=${companyId}`),
  create: (data) => api.post('/subsidiaries', data),
  update: (id, data) => api.patch(`/subsidiaries/${id}`, data),
  delete: (id) => api.delete(`/subsidiaries/${id}`),
};

// Leave Management API
export const leaveAPI = {
  // Leave Types
  getTypes: (companyId) => api.get(`/leave/types?company_id=${companyId}`),
  createType: (data) => api.post('/leave/types', data),
  // Leave Requests
  getRequests: (companyId, status = '', employeeId = '') => 
    api.get(`/leave/requests?company_id=${companyId}${status ? `&status=${status}` : ''}${employeeId ? `&employee_id=${employeeId}` : ''}`),
  createRequest: (data) => api.post('/leave/requests', data),
  approve: (requestId, approvalType) => api.post(`/leave/requests/${requestId}/approve?approval_type=${approvalType}`),
  reject: (requestId, reason = '') => api.post(`/leave/requests/${requestId}/reject${reason ? `?reason=${reason}` : ''}`),
  getBalance: (employeeId) => api.get(`/leave/balance/${employeeId}`),
};

// Recruitment API
export const recruitmentAPI = {
  // Job Postings
  getJobs: (companyId, status = '') => api.get(`/recruitment/jobs?company_id=${companyId}${status ? `&status=${status}` : ''}`),
  createJob: (data) => api.post('/recruitment/jobs', data),
  updateJob: (jobId, data) => api.patch(`/recruitment/jobs/${jobId}`, data),
  // Applicants
  getApplicants: (companyId, jobId = '', status = '') => 
    api.get(`/recruitment/applicants?company_id=${companyId}${jobId ? `&job_id=${jobId}` : ''}${status ? `&status=${status}` : ''}`),
  createApplicant: (data) => api.post('/recruitment/applicants', data),
  updateApplicant: (id, data) => api.patch(`/recruitment/applicants/${id}`, data),
  convertToUser: (id, role) => api.post(`/recruitment/applicants/${id}/convert-to-user?role=${role}`),
};

// RBAC API
export const rbacAPI = {
  getPermissionsMatrix: () => api.get('/rbac/permissions-matrix'),
  getRoles: (companyId) => api.get(`/rbac/roles?company_id=${companyId}`),
  createRole: (data) => api.post('/rbac/roles', data),
  updateRole: (id, data) => api.patch(`/rbac/roles/${id}`, data),
};

// Employees API
export const employeesAPI = {
  getAll: (companyId) => api.get(`/employees?company_id=${companyId}`),
  getById: (id) => api.get(`/employees/${id}`),
  create: (data) => api.post('/employees', data),
  update: (id, data) => api.patch(`/employees/${id}`, data),
};

// Payroll API
export const payrollAPI = {
  // Settings
  getSettings: (companyId) => api.get(`/payroll/settings?company_id=${companyId}`),
  saveSettings: (data) => api.post('/payroll/settings', data),
  getAIRates: (country = 'Liberia') => api.get(`/payroll/ai-rates?country=${country}`),
  getCBLRate: () => api.get('/payroll/cbl-rate'),
  calculateTax: (salary, currency = 'USD', exchangeRate = null) => 
    api.get(`/payroll/tax-calculator?monthly_salary=${salary}&currency=${currency}${exchangeRate ? `&exchange_rate=${exchangeRate}` : ''}`),
  // Dashboard
  getDashboard: (companyId) => api.get(`/payroll/dashboard?company_id=${companyId}`),
  // Payroll Runs
  getRuns: (companyId) => api.get(`/payroll/runs?company_id=${companyId}`),
  getRun: (runId) => api.get(`/payroll/runs/${runId}`),
  createRun: (data) => api.post('/payroll/runs', data),
  approveRun: (runId) => api.post(`/payroll/runs/${runId}/approve`),
};

// Accounts API
export const accountsAPI = {
  getAll: (companyId) => api.get(`/accounts?company_id=${companyId}`),
  getById: (id) => api.get(`/accounts/${id}`),
  create: (data) => api.post('/accounts', data),
  update: (id, data) => api.patch(`/accounts/${id}`, data),
  delete: (id) => api.delete(`/accounts/${id}`),
};

// Journal Entries API
export const journalEntriesAPI = {
  getAll: (companyId) => api.get(`/journal-entries?company_id=${companyId}`),
  create: (data) => api.post('/journal-entries', data),
  approve: (id) => api.patch(`/journal-entries/${id}/approve`),
  post: (id) => api.patch(`/journal-entries/${id}/post`),
};

// Budgets API (Legacy)
export const budgetsAPI = {
  getAll: (companyId) => api.get(`/budgets?company_id=${companyId}`),
  create: (data) => api.post('/budgets', data),
};

// Financial Reports API
export const reportsAPI = {
  getGeneralLedger: (companyId, params = {}) => {
    const queryParams = new URLSearchParams({ company_id: companyId });
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    if (params.account_id) queryParams.append('account_id', params.account_id);
    return api.get(`/reports/general-ledger?${queryParams}`);
  },
  getTrialBalance: (companyId, asOfDate = null) => {
    const queryParams = new URLSearchParams({ company_id: companyId });
    if (asOfDate) queryParams.append('as_of_date', asOfDate);
    return api.get(`/reports/trial-balance?${queryParams}`);
  },
  getIncomeStatement: (companyId, params = {}) => {
    const queryParams = new URLSearchParams({ company_id: companyId });
    if (params.start_date) queryParams.append('start_date', params.start_date);
    if (params.end_date) queryParams.append('end_date', params.end_date);
    return api.get(`/reports/income-statement?${queryParams}`);
  },
  getBalanceSheet: (companyId, asOfDate = null) => {
    const queryParams = new URLSearchParams({ company_id: companyId });
    if (asOfDate) queryParams.append('as_of_date', asOfDate);
    return api.get(`/reports/balance-sheet?${queryParams}`);
  },
  // PDF exports
  downloadPDF: (endpoint, params = {}) => {
    const queryParams = new URLSearchParams(params);
    return api.get(`${endpoint}?${queryParams}`, { responseType: 'blob' });
  },
};

// User Management API
export const usersAPI = {
  getAll: (companyId = null) => {
    const params = companyId ? `?company_id=${companyId}` : '';
    return api.get(`/users${params}`);
  },
  getById: (id) => api.get(`/users/${id}`),
  create: (data) => api.post('/users', data),
  update: (id, data) => api.patch(`/users/${id}`, data),
  delete: (id) => api.delete(`/users/${id}`),
  getRoles: () => api.get('/roles'),
  resetPassword: (id, newPassword) => api.post(`/users/${id}/reset-password`, { new_password: newPassword }),
};

// Profitability Budget API (New)
export const profitBudgetAPI = {
  // Budget Years
  getYears: (companyId) => api.get(`/budget-years?company_id=${companyId}`),
  createYear: (companyId, data) => api.post(`/budget-years?company_id=${companyId}`, data),
  getYear: (yearId) => api.get(`/budget-years/${yearId}`),
  
  // Budget Accounts
  createAccount: (yearId, data) => api.post(`/budget-years/${yearId}/accounts`, data),
  updateAccount: (accountId, data) => api.patch(`/budget-accounts/${accountId}`, data),
  deleteAccount: (accountId) => api.delete(`/budget-accounts/${accountId}`),
  updateMonthly: (accountId, month, amount, isOverride = true) => 
    api.patch(`/budget-accounts/${accountId}/monthly/${month}?amount=${amount}&is_override=${isOverride}`),
  distributeEvenly: (accountId) => api.post(`/budget-accounts/${accountId}/distribute`),
  
  // Performance
  getPerformance: (yearId) => api.get(`/budget-performance/${yearId}`),
  
  // AI Validation
  validate: (yearId) => api.post(`/budget-ai/validate/${yearId}`),
  explainCalculation: (accountId) => api.post(`/budget-ai/explain-calculation?account_id=${accountId}`),
};

// Dashboard API
export const dashboardAPI = {
  getStats: (companyId) => api.get(`/dashboard/stats?company_id=${companyId}`),
  getActivity: (companyId) => api.get(`/dashboard/activity?company_id=${companyId}`),
};

// Consolidation API
export const consolidationAPI = {
  getData: (parentCompanyId) => api.get(`/consolidation?parent_company_id=${parentCompanyId}`),
};

// Admin API
export const adminAPI = {
  getUsers: () => api.get('/admin/users'),
};

// ==================== DFS APIs ====================

// DFS Cash API -- cash sources are Chart-of-Accounts asset accounts
// (accountsAPI) directly; there is no separate vault entity/endpoints.
export const cashAPI = {
  getCashTraceability: (companyId) => api.get(`/dfs/cash-traceability?company_id=${companyId}`),
  getCashPosition: (companyId) => api.get(`/dfs/cash-position?company_id=${companyId}`),
};

// DFS Cash Accounts API (Bank, Petty Cash, Mobile Money)
export const cashAccountsAPI = {
  getAll: (companyId, accountType = null) => api.get(`/dfs/cash-accounts?company_id=${companyId}${accountType ? `&account_type=${accountType}` : ''}`),
  create: (data) => api.post('/dfs/cash-accounts', data),
  update: (id, data) => api.patch(`/dfs/cash-accounts/${id}`, data),
  transaction: (accountId, data) => api.post(`/dfs/cash-accounts/${accountId}/transaction`, data),
  getTransactions: (accountId, limit = 100) => api.get(`/dfs/cash-accounts/${accountId}/transactions?limit=${limit}`),
};

// DFS Dashboard API
export const dfsDashboardAPI = {
  getStats: (companyId, date) => api.get(`/dfs/dashboard?company_id=${companyId}${date ? `&date=${date}` : ''}`),
};

// DFS Teller Account API
export const tellerAccountAPI = {
  getAll: (companyId) => api.get(`/dfs/teller-accounts?company_id=${companyId}`),
  getById: (id) => api.get(`/dfs/teller-accounts/${id}`),
  getMyAccount: () => api.get('/dfs/my-teller-account'),
  create: (data) => api.post('/dfs/teller-accounts', data),
  update: (id, data) => api.patch(`/dfs/teller-accounts/${id}`, data),
  getTellerUsers: (companyId) => api.get(`/dfs/teller-users?company_id=${companyId}`),
};

// DFS Vault Allocation API
export const allocationAPI = {
  getAll: (companyId, status) => api.get(`/dfs/allocations?company_id=${companyId}${status ? `&status=${status}` : ''}`),
  create: (data) => api.post('/dfs/allocations', data),
  acknowledge: (id) => api.patch(`/dfs/allocations/${id}/acknowledge`),
  cancel: (id) => api.patch(`/dfs/allocations/${id}/cancel`),
};

// DFS Teller Session API
export const sessionAPI = {
  getAll: (tellerId, status) => api.get(`/dfs/sessions?${tellerId ? `teller_account_id=${tellerId}&` : ''}${status ? `status=${status}` : ''}`),
  getCurrent: (tellerId) => api.get(`/dfs/sessions/current/${tellerId}`),
  open: (data) => api.post('/dfs/sessions/open', data),
  close: (sessionId, data) => api.post(`/dfs/sessions/${sessionId}/close`, data),
};

// DFS Teller Transaction API
export const tellerTransactionAPI = {
  getAll: (sessionId, tellerId) => api.get(`/dfs/transactions?${sessionId ? `session_id=${sessionId}&` : ''}${tellerId ? `teller_account_id=${tellerId}` : ''}`),
  create: (data) => api.post('/dfs/transactions', data),
};

// DFS Stock API
export const stockAPI = {
  getItems: (companyId) => api.get(`/dfs/stock-items?company_id=${companyId}`),
  createItem: (data) => api.post('/dfs/stock-items', data),
  itemTransaction: (itemId, data) => api.post(`/dfs/stock-items/${itemId}/transaction`, data),
  getAllocations: (companyId, status) => api.get(`/dfs/stock-allocations?company_id=${companyId}${status ? `&status=${status}` : ''}`),
  createAllocation: (data) => api.post('/dfs/stock-allocations', data),
};

// DFS Customer API
export const customerAPI = {
  getAll: (companyId, search) => api.get(`/dfs/customers?company_id=${companyId}${search ? `&search=${search}` : ''}`),
  getById: (id) => api.get(`/dfs/customers/${id}`),
  create: (data) => api.post('/dfs/customers', data),
};

// DFS Loan API
export const loanAPI = {
  getAll: (companyId, status, customerId) => api.get(`/dfs/loans?company_id=${companyId}${status ? `&status=${status}` : ''}${customerId ? `&customer_id=${customerId}` : ''}`),
  getById: (id) => api.get(`/dfs/loans/${id}`),
  create: (data) => api.post('/dfs/loans', data),
  approve: (id) => api.patch(`/dfs/loans/${id}/approve`),
  disburse: (id) => api.patch(`/dfs/loans/${id}/disburse`),
  makePayment: (id, data) => api.post(`/dfs/loans/${id}/payment`, data),
  getAmortization: (id) => api.get(`/dfs/loans/${id}/amortization`),
  getSourceAccounts: (companyId) => api.get(`/dfs/loan-source-accounts?company_id=${companyId}`),
  uploadDocument: (file) => {
    const formData = new FormData();
    formData.append('file', file);
    return api.post('/dfs/loans/upload-document', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  getDocumentUrl: (filename) => `${BACKEND_URL}/api/dfs/loans/documents/${filename}`,
  downloadReceipt: (loanId, paymentId) => api.get(`/dfs/loans/${loanId}/payments/${paymentId}/receipt`, { responseType: 'blob' }),
};

// DFS Cash Position API
export const cashPositionAPI = {
  get: (companyId) => api.get(`/dfs/cash-position?company_id=${companyId}`),
};

// DFS SMS Parsing API (AI-powered)
export const smsParseAPI = {
  parse: (smsText) => api.post('/dfs/parse-sms', { sms_text: smsText }),
};

// DFS Enhanced Session API
export const tellerSessionAPI = {
  getAll: (tellerId, status) => api.get(`/dfs/sessions?${tellerId ? `teller_account_id=${tellerId}&` : ''}${status ? `status=${status}` : ''}`),
  getCurrent: (tellerId) => api.get(`/dfs/sessions/current/${tellerId}`),
  open: (data) => api.post('/dfs/sessions/open', data),
  openWithConfirmation: (data) => api.post('/dfs/sessions/open-with-confirmation', data),
  close: (sessionId, data) => api.post(`/dfs/sessions/${sessionId}/close`, data),
  getSummary: (sessionId) => api.get(`/dfs/sessions/${sessionId}/summary`),
};

// DFS Stock Sales API
export const stockSalesAPI = {
  create: (sessionId, itemId, quantity, unitPrice, currency = 'USD', customerName = null, notes = null) => 
    api.post(`/dfs/stock-sales?session_id=${sessionId}&item_id=${itemId}&quantity=${quantity}&unit_price=${unitPrice}&currency=${currency}${customerName ? `&customer_name=${customerName}` : ''}${notes ? `&notes=${notes}` : ''}`),
  getTellerStock: (tellerId) => api.get(`/dfs/teller-stock/${tellerId}`),
};

// DFS Cash Request API
export const cashRequestAPI = {
  create: (data) => api.post('/dfs/cash-requests', data),
  getAll: (tellerId = null, status = null) => api.get(`/dfs/cash-requests?${tellerId ? `teller_account_id=${tellerId}&` : ''}${status ? `status=${status}` : ''}`),
};
